/**
 * Reload-surviving diagnostics ring buffer (#5105 — native team-member-only
 * login loop).
 *
 * Why this exists: the member-only login loop strobes the WKWebView (reload
 * cycle) on native iOS, wiping in-memory state AND `sessionStorage` on every
 * cycle — so a live-only console can never see the pattern. This buffer persists
 * to `localStorage` under a `xavior:diag:*` key that is deliberately NOT part of
 * `AUTH_STORAGE_KEYS`, so `clearAuthStorage()` (which fires on every
 * unauthenticated transition) does NOT wipe it. The log therefore accumulates
 * ACROSS reload cycles and can be read after the fact from the DiagPanel.
 *
 * NEVER log secrets: access/refresh tokens, raw JWTs, Authorization headers.
 * Emails are masked by `redact()`; keys matching token/secret/password are
 * dropped. Diagnostics must never throw — every entry point swallows errors.
 */
export interface DiagEntry {
  /** epoch ms */
  t: number;
  /** short channel — e.g. 'gate', 'containers', 'auth', 'api' */
  tag: string;
  msg: string;
  data?: unknown;
}

const KEY = 'xavior:diag:log';
const MAX_ENTRIES = 300;

function read(): DiagEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DiagEntry[]) : [];
  } catch {
    return [];
  }
}

function write(entries: DiagEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // quota / private mode — best effort
  }
}

// Keys whose VALUES are dropped wholesale (case-insensitive substring match).
const SECRET_KEY_RE =
  /token|secret|password|authorization|jwt|bearer|cookie|api[-_]?key|credential|session[-_]?id/i;
// JWT-shaped values get scrubbed even under an innocuous key.
const JWT_VALUE_RE = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;

/** Scrub a string: drop JWT-shaped tokens, mask email local-parts. */
function redactString(s: string): string {
  return s
    .replace(JWT_VALUE_RE, '[redacted-jwt]')
    .replace(/([a-zA-Z0-9._%+-])[a-zA-Z0-9._%+-]*(@[^\s,;]+)/g, '$1***$2');
}

/** Mask emails/JWTs and drop token/secret-shaped keys before persisting. */
function redact(value: unknown): unknown {
  if (typeof value === 'string') return redactString(value);
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Only STRING values under a secret-shaped key are dropped — a boolean/number
      // (e.g. `hadKeychainToken: true`) is a diagnostic flag, not a secret, and must
      // survive so we can actually read the signal. Strings still recurse through
      // redact() for JWT/email scrubbing.
      out[k] =
        SECRET_KEY_RE.test(k) && typeof v === 'string' ? '[redacted]' : redact(v);
    }
    return out;
  }
  return value;
}

/** Append a diagnostics entry. Cheap, never throws, mirrors to console.
 *  Both `msg` and `data` are redacted — the console mirror must not leak what
 *  the persisted buffer redacts (WKWebView console output can be captured).
 *  The mirror uses console.info (NOT console.log) so it survives the prod
 *  build's console.log/debug stripping (vite.config.ts esbuild.pure, #5264). */
export function diag(tag: string, msg: string, data?: unknown): void {
  try {
    const safeMsg = redactString(msg);
    const safeData = data !== undefined ? redact(data) : undefined;
    const entry: DiagEntry = {
      t: Date.now(),
      tag,
      msg: safeMsg,
      ...(safeData !== undefined ? { data: safeData } : {}),
    };
    const entries = read();
    entries.push(entry);
    write(entries.length > MAX_ENTRIES ? entries.slice(-MAX_ENTRIES) : entries);
    console.info(`[diag:${tag}] ${safeMsg}`, safeData ?? '');
  } catch {
    // diagnostics must never break a flow
  }
}

/** Newest-last list of buffered entries. */
export function getDiagLog(): DiagEntry[] {
  return read();
}

export function clearDiagLog(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// --- Shared container / session snapshot shapes (one definition, #5105) -------
// Used by the gate-state diag (App), the containers diag (SessionProvider), and
// the DiagPanel summary — so the redaction-safe field set lives in exactly one
// place instead of being hand-duplicated at each call site.

export interface DiagContainerSummary {
  id: string;
  isOwner?: boolean;
  permission?: string;
  role?: string;
  type?: string;
  onboardingCompleted?: boolean;
}

/** Project a container to the redaction-safe fields we log/show (no PII).
 *  Accepts any superset (ContainerAccess) via structural assignability. */
export function summarizeContainer(c: DiagContainerSummary): DiagContainerSummary {
  return {
    id: c.id,
    isOwner: c.isOwner,
    permission: c.permission,
    role: c.role,
    type: c.type,
    onboardingCompleted: c.onboardingCompleted,
  };
}

/** Live session snapshot shown in the DiagPanel — built in a composition layer
 *  (app/DiagHost) so `common/` never imports `features/auth` (import-direction). */
export interface DiagSnapshot {
  authStatus: string;
  hasToken: boolean;
  hasProfile: boolean;
  containersLoaded: boolean;
  containerReady: boolean;
  containersCount: number;
  containers: DiagContainerSummary[];
  selected: DiagContainerSummary | null;
}
